'use client'

import Link from 'next/link'
import {
  Volume2,
  AlignLeft,
  Layers,
  Table2,
  Clock,
  Shield,
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  Sparkles,
  CircleCheck,
} from 'lucide-react'
import {
  LenisFadeIn,
  LenisStaggered,
} from '@/components/animations/LenisReveal'
import AeoOverallScore from './AeoOverallScore'
import AeoScoreCard from './AeoScoreCard'
import PlatformCTA from '@/components/platform/PlatformCTA'

interface CategoryResult {
  score: number
  maxScore: number
  color: string
  findings: { text: string; status: 'good' | 'warning' | 'bad' }[]
}

interface AeoAuditResult {
  url: string
  domain: string
  checkedAt: string
  overallScore: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
  categories: {
    speakableSchema: CategoryResult
    answerFirstStructure: CategoryResult
    structuredDataQuality: CategoryResult
    dataExtractability: CategoryResult
    contentFreshness: CategoryResult
    entityAuthority: CategoryResult
  }
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
}

interface AeoResultsProps {
  result: AeoAuditResult
  /**
   * 'public' (default) renders the full marketing surface. 'dashboard' hides
   * public-only chrome (`PlatformCTA` + the gradient services CTA) so the SAME
   * rich report can be reused inside `/platform/app`. The result content (score
   * ring, AI analysis, 6 categories, recommendations) is identical either way.
   */
  variant?: 'public' | 'dashboard'
}

const CATEGORY_META: {
  key: keyof AeoAuditResult['categories']
  label: string
  icon: React.ReactNode
}[] = [
  {
    key: 'speakableSchema',
    label: 'Speakable Schema',
    icon: <Volume2 size={18} />,
  },
  {
    key: 'answerFirstStructure',
    label: 'Answer-First Structure',
    icon: <AlignLeft size={18} />,
  },
  {
    key: 'structuredDataQuality',
    label: 'Structured Data Quality',
    icon: <Layers size={18} />,
  },
  {
    key: 'dataExtractability',
    label: 'Data Extractability',
    icon: <Table2 size={18} />,
  },
  {
    key: 'contentFreshness',
    label: 'Content Freshness',
    icon: <Clock size={18} />,
  },
  {
    key: 'entityAuthority',
    label: 'Entity Authority',
    icon: <Shield size={18} />,
  },
]

const PRIORITY_ICONS: Record<string, React.ReactNode> = {
  high: <AlertTriangle size={14} style={{ color: '#F90B8A' }} />,
  medium: <AlertCircle size={14} style={{ color: '#FDC304' }} />,
  low: <Info size={14} style={{ color: '#008dcc' }} />,
}

const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: '#F90B8A15', text: '#F90B8A' },
  medium: { bg: '#FDC30415', text: '#FDC304' },
  low: { bg: '#008dcc15', text: '#008dcc' },
}

const BLOG_LINKS: Record<string, string> = {
  'Speakable Schema':
    '/blogs/how-we-optimized-21-posts-for-ai-citation-aeo-implementation',
  'Content Structure':
    '/blogs/how-we-optimized-21-posts-for-ai-citation-aeo-implementation',
  'Structured Data': '/blogs/knowledge-graph-llm-visibility-real-data',
  'Data Extractability':
    '/blogs/how-we-optimized-21-posts-for-ai-citation-aeo-implementation',
  'Content Freshness': '/blogs/build-brand-ai-search-engines-cite',
  'Entity Authority': '/blogs/knowledge-graph-llm-visibility-real-data',
}

export default function AeoResults({
  result,
  variant = 'public',
}: AeoResultsProps) {
  return (
    <div className='space-y-8'>
      {/* Overall Score */}
      <LenisFadeIn>
        <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
          <div className='text-center mb-2'>
            <p className='text-xs text-muted-foreground'>AEO Page Audit for</p>
            <p className='text-sm font-medium text-foreground truncate max-w-lg mx-auto'>
              {result.url}
            </p>
          </div>
          <AeoOverallScore score={result.overallScore} grade={result.grade} />
        </div>
      </LenisFadeIn>

      {variant !== 'dashboard' && (
        <PlatformCTA
          currentTool='AEO Page Auditor'
          score={result.overallScore}
          grade={result.grade}
        />
      )}

      {/* AI Analysis */}
      {result.aiSummary && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4 flex items-center gap-2'>
              <Sparkles size={20} style={{ color: '#8b5cf6' }} />
              AI Analysis
            </h2>

            <p className='text-sm text-foreground/90 leading-relaxed mb-4'>
              {result.aiSummary.summary}
            </p>

            {result.aiSummary.actions.length > 0 && (
              <div className='space-y-2 mb-4'>
                <h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
                  Priority Actions
                </h3>
                <ul className='space-y-2'>
                  {result.aiSummary.actions.map((action, i) => (
                    <li
                      key={i}
                      className='flex items-start gap-2.5 text-sm text-foreground/80'
                    >
                      <CircleCheck
                        size={16}
                        className='mt-0.5 shrink-0'
                        style={{ color: '#008dcc' }}
                      />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              className='rounded-lg p-3 text-xs text-foreground/70 leading-relaxed'
              style={{
                backgroundColor: '#8b5cf610',
                border: '1px solid #8b5cf620',
              }}
            >
              {result.aiSummary.context}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* Category Score Cards */}
      <LenisStaggered>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {CATEGORY_META.map(m => {
            const cat = result.categories[m.key]
            return (
              <AeoScoreCard
                key={m.key}
                title={m.label}
                icon={m.icon}
                score={cat.score}
                maxScore={cat.maxScore}
                findings={cat.findings}
                color={cat.color}
              />
            )
          })}
        </div>
      </LenisStaggered>

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4'>
              AEO Action Roadmap
            </h2>
            <div className='space-y-3'>
              {result.recommendations.map((rec, i) => {
                const pc = PRIORITY_COLORS[rec.priority]
                const blogLink = BLOG_LINKS[rec.category]
                return (
                  <div
                    key={i}
                    className='flex items-start gap-3 bg-background border border-border/60 rounded-xl p-4'
                  >
                    <div className='mt-0.5'>{PRIORITY_ICONS[rec.priority]}</div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 mb-1'>
                        <span
                          className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5'
                          style={{ backgroundColor: pc.bg, color: pc.text }}
                        >
                          {rec.priority}
                        </span>
                        <span className='text-[11px] text-muted-foreground'>
                          {rec.category}
                        </span>
                      </div>
                      <p className='text-sm text-foreground/80'>
                        {rec.message}
                      </p>
                      {blogLink && (
                        <a
                          href={blogLink}
                          className='inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1.5'
                        >
                          Learn more <ArrowRight size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* CTA — public surface only */}
      {variant !== 'dashboard' && (
        <LenisFadeIn>
          <div
            className='relative overflow-hidden rounded-2xl p-8 md:p-10 text-center'
            style={{
              background: 'linear-gradient(135deg, #4c1d95, #7c3aed, #a78bfa)',
            }}
          >
            <h3 className='text-xl md:text-2xl font-bold text-white mb-3'>
              Want to improve your AEO score?
            </h3>
            <p
              className='text-sm mb-6 max-w-lg mx-auto'
              style={{ color: 'rgba(255,255,255,0.9)' }}
            >
              We retrofit content with speakable schema, StatBlocks,
              entity-linked structured data, and answer-first formatting. We did
              it for 21 posts in one session.
            </p>
            <div className='flex flex-wrap items-center justify-center gap-3'>
              <a
                href='/services/ai-visibility-strategy'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full border-2 border-white text-white font-medium text-sm transition-all duration-300'
                style={{ backgroundColor: 'transparent' }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'white'
                  e.currentTarget.style.color = '#7c3aed'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = 'white'
                }}
              >
                Get an AEO Audit
              </a>
              <Link
                href='/blogs/how-we-optimized-21-posts-for-ai-citation-aeo-implementation'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full text-sm font-medium transition-colors'
                style={{ color: 'rgba(255,255,255,0.9)' }}
              >
                See our case study <ArrowRight size={16} className='ml-1' />
              </Link>
            </div>
          </div>
        </LenisFadeIn>
      )}
    </div>
  )
}
